import React from 'react';
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import PropTypes from 'prop-types';
import { applyTextStyles, useTheme, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, selectTokens, variantProp, viewProps } from '../utils';
import LabelContent from './LabelContent';
import Tooltip from '../Tooltip';
import tooltipPropTypes from '../Tooltip/shared';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectLabelStyles = (tokens, themeOptions) => applyTextStyles({
  ...selectTokens('Typography', tokens),
  themeOptions
});
const selectHintStyles = (_ref, themeOptions) => {
  let {
    hintColor,
    hintFontName,
    hintFontSize,
    hintFontWeight,
    hintLineHeight
  } = _ref;
  return applyTextStyles({
    color: hintColor,
    fontName: hintFontName,
    fontSize: hintFontSize,
    fontWeight: hintFontWeight,
    lineHeight: hintLineHeight,
    themeOptions
  });
};
const selectGapStyles = _ref2 => {
  let {
    gap
  } = _ref2;
  return {
    marginRight: gap
  };
};
const InputLabel = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    copy = 'en',
    label,
    forId,
    hint,
    hintPosition = 'inline',
    hintId,
    tooltip,
    tokens,
    variant,
    characterCount,
    maxCharacterAllowed,
    ...rest
  } = _ref3;
  const themeTokens = useThemeTokens('InputLabel', tokens, variant);
  const hasTooltip = tooltip !== undefined;
  const isHintInline = hintPosition === 'inline';
  const {
    themeOptions
  } = useTheme();
  const getTooltipProps = () => {
    return typeof tooltip === 'string' ? {
      content: tooltip
    } : tooltip;
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(View, {
      ref: ref,
      style: staticStyles.container,
      ...selectProps(rest),
      children: [/*#__PURE__*/_jsx(Text, {
        style: [selectLabelStyles(themeTokens, themeOptions), selectGapStyles(themeTokens), staticStyles.label],
        children: /*#__PURE__*/_jsx(LabelContent, {
          forId: forId,
          children: label
        })
      }), hint && isHintInline && /*#__PURE__*/_jsx(Text, {
        style: [selectHintStyles(themeTokens, themeOptions), hasTooltip && selectGapStyles(themeTokens), staticStyles.label],
        nativeID: hintId,
        children: hint
      }), hasTooltip && /*#__PURE__*/_jsx(View, {
        style: [staticStyles.tooltipAlign, {
          height: themeTokens.fontSize * themeTokens.lineHeight,
          color: themeTokens.color
        }],
        children: /*#__PURE__*/_jsx(Tooltip, {
          copy: copy,
          ...getTooltipProps()
        })
      }), maxCharacterAllowed && isHintInline && /*#__PURE__*/_jsxs(Text, {
        style: [selectHintStyles({
          ...themeTokens
        }), staticStyles.characterCountlabel],
        children: [characterCount, "/", maxCharacterAllowed]
      })]
    }), hint && !maxCharacterAllowed && !isHintInline && /*#__PURE__*/_jsx(Text, {
      style: [selectHintStyles(themeTokens), staticStyles.hintBelow],
      nativeID: hintId,
      children: hint
    }), hint && maxCharacterAllowed && !isHintInline && /*#__PURE__*/_jsxs(View, {
      style: staticStyles.container,
      children: [/*#__PURE__*/_jsx(Text, {
        style: [selectHintStyles(themeTokens), staticStyles.flexHintBelow],
        nativeID: hintId,
        children: hint
      }), /*#__PURE__*/_jsxs(Text, {
        style: [selectHintStyles(themeTokens), staticStyles.characterCountlabel],
        children: [characterCount, "/", maxCharacterAllowed]
      })]
    })]
  });
});
InputLabel.displayName = 'InputLabel';
InputLabel.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: PropTypes.oneOf(['en', 'fr']),
  /**
   * The input label.
   */
  label: PropTypes.string.isRequired,
  /**
   * The id attribute of the input which this label refers to.
   */
  forId: PropTypes.string,
  /**
   * A short description of the expected input.
   */
  hint: PropTypes.string,
  /**
   * Position of the hint relative to label. Use `below` to display a larger hint below the label.
   */
  hintPosition: PropTypes.oneOf(['inline', 'below']),
  /**
   * The id attribute passed down to the hint element.
   */
  hintId: PropTypes.string,
  /**
   * Content of an optional `Tooltip`. If set, a tooltip button will be shown next to the label.
   */
  tooltip: PropTypes.oneOfType([PropTypes.shape(tooltipPropTypes), PropTypes.string]),
  /**
   * Current number of characterts of an input text.
   */
  characterCount: PropTypes.number,
  /**
   * Max number of characters that allows an input text.
   */
  maxCharacterAllowed: PropTypes.number,
  tokens: getTokensPropType('InputLabel'),
  variant: variantProp.propType
};
export default InputLabel;
const staticStyles = StyleSheet.create({
  container: {
    flexShrink: 1,
    flexDirection: 'row'
  },
  characterCountlabel: {
    marginLeft: 'auto',
    marginTop: 'auto'
  },
  label: {
    flexShrink: 1,
    alignSelf: 'center'
  },
  hintBelow: {
    flexBasis: '100%',
    flexShrink: 0
  },
  flexHintBelow: {
    flexBasis: '100%',
    flexShrink: 1
  },
  tooltipAlign: {
    alignSelf: 'center',
    justifyContent: 'center'
  }
});